/* ==========================================================================
   Article Festive Gifts — progressive enhancement.

   Loaded by `snippets/article-editorial-styles.liquid`, which every
   `article-editorial-*` section renders. Duplicate <script src> tags each
   still EXECUTE, so this file guards itself against running twice.

   Everything here is an enhancement: the table of contents is plain anchor
   links, the FAQ is native <details>, and the finder falls back to its
   default recommendation. Nothing breaks without JS.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__afgFestiveGiftsInit) return;
  window.__afgFestiveGiftsInit = true;

  /* ---- Table of contents: highlight the section currently in view -------- */

  function initTableOfContents(root) {
    var links = Array.prototype.slice.call(root.querySelectorAll('[data-ae-toc-link]'));
    if (!links.length || !('IntersectionObserver' in window)) return;

    var byId = {};
    var targets = [];

    links.forEach(function (link) {
      var id = (link.getAttribute('href') || '').replace(/^#/, '');
      if (!id) return;
      var target = document.getElementById(id);
      if (!target) return;
      byId[id] = link;
      targets.push(target);
    });

    if (!targets.length) return;

    var visible = [];

    function setCurrent(id) {
      links.forEach(function (link) {
        link.setAttribute('aria-current', byId[id] === link ? 'true' : 'false');
      });
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var id = entry.target.id;
          var index = visible.indexOf(id);
          if (entry.isIntersecting && index === -1) {
            visible.push(id);
          } else if (!entry.isIntersecting && index > -1) {
            visible.splice(index, 1);
          }
        });

        if (!visible.length) return;

        // Whichever visible heading sits highest on the page wins.
        var top = visible
          .map(function (id) {
            return { id: id, y: document.getElementById(id).getBoundingClientRect().top };
          })
          .sort(function (a, b) {
            return a.y - b.y;
          })[0];

        setCurrent(top.id);
      },
      { rootMargin: '-20% 0px -65% 0px', threshold: 0 }
    );

    targets.forEach(function (target) {
      observer.observe(target);
    });
  }

  /* ---- Finder: two questions, one recommendation ------------------------- */

  function initFinder(finder) {
    var groups = Array.prototype.slice.call(finder.querySelectorAll('[data-ae-finder-group]'));
    var result = finder.querySelector('[data-ae-finder-result]');
    var payload = finder.querySelector('[data-ae-finder-data]');
    if (!groups.length || !result || !payload) return;

    var products;
    try {
      products = JSON.parse(payload.textContent);
    } catch (err) {
      products = [];
    }
    if (!Array.isArray(products)) products = [];

    var choices = groups.map(function (group) {
      var pressed = group.querySelector('[aria-pressed="true"]');
      var options = Array.prototype.slice.call(group.querySelectorAll('[data-ae-finder-option]'));
      return Math.max(0, options.indexOf(pressed));
    });

    function render() {
      // Nothing recommended yet: the answer buttons still respond, the panel
      // just keeps whatever the section rendered.
      if (!products.length) return;

      // Row-major across the question grid, clamped to what the merchant set.
      var index = 0;
      var stride = 1;
      for (var i = groups.length - 1; i >= 0; i--) {
        index += choices[i] * stride;
        stride *= groups[i].querySelectorAll('[data-ae-finder-option]').length || 1;
      }

      var product = products[Math.min(index, products.length - 1)];
      if (!product) return;

      // `cta-label` rather than `cta`: the latter is the anchor itself, and
      // writing text into it would throw away the span this targets.
      var fields = {
        eyebrow: product.eyebrow,
        title: product.title,
        text: product.text,
        'cta-label': product.cta_label
      };

      Object.keys(fields).forEach(function (key) {
        var node = result.querySelector('[data-ae-finder-' + key + ']');
        if (node && typeof fields[key] === 'string') node.textContent = fields[key];
      });

      var cta = result.querySelector('[data-ae-finder-cta]');
      if (cta && product.url) cta.setAttribute('href', product.url);

      var media = result.querySelector('[data-ae-finder-media]');
      if (media) {
        if (product.image) {
          media.innerHTML =
            '<img src="' +
            product.image +
            '" alt="" width="180" height="180" loading="lazy">';
        } else {
          media.innerHTML =
            '<p class="ae-media__note">' + (product.image_note || '') + '</p>';
        }
      }
    }

    groups.forEach(function (group, groupIndex) {
      group.addEventListener('click', function (event) {
        var option = event.target.closest('[data-ae-finder-option]');
        if (!option || !group.contains(option)) return;

        var options = Array.prototype.slice.call(group.querySelectorAll('[data-ae-finder-option]'));
        options.forEach(function (candidate) {
          candidate.setAttribute('aria-pressed', candidate === option ? 'true' : 'false');
        });

        choices[groupIndex] = options.indexOf(option);
        render();
      });
    });

    render();
  }

  /* ---- Add to cart ------------------------------------------------------- */

  function alpineStore(name) {
    return window.Alpine && typeof window.Alpine.store === 'function'
      ? window.Alpine.store(name)
      : null;
  }

  /* Delegated, so it covers sections rendered later in the theme editor too.
     Mirrors what the theme's own product cards do: add through the cart store,
     then open the drawer. */
  function initCartForms() {
    document.addEventListener('submit', function (event) {
      var form = event.target.closest && event.target.closest('[data-ae-cart-form]');
      if (!form) return;

      var cart = alpineStore('cart');
      // Without the theme's cart store there is nothing to intercept with, so
      // let the browser post the form and land on the cart page as before.
      if (!cart || typeof cart.addToCart !== 'function') return;

      var id = form.querySelector('input[name="id"]');
      if (!id || !id.value) return;

      event.preventDefault();

      var button = form.querySelector('button[type="submit"]');
      if (button) {
        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
      }

      function done() {
        if (!button) return;
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }

      Promise.resolve(cart.addToCart({ id: id.value, quantity: 1 }))
        .then(function () {
          var ui = alpineStore('ui');
          if (ui && typeof ui.openCartDrawer === 'function') ui.openCartDrawer();
        })
        .catch(function () {
          // The cart service logs its own failures; just release the button.
        })
        .then(done, done);
    });
  }

  /* ---- Sticky shop bar --------------------------------------------------- */

  function initShopBar(bar) {
    var items = Array.prototype.slice.call(bar.querySelectorAll('[data-ae-shop-item]'));
    if (!items.length) return;

    var storageKey = 'ae-shop-bar-dismissed';
    var dismissed = false;
    try {
      dismissed = sessionStorage.getItem(storageKey) === '1';
    } catch (err) {
      // Private browsing and blocked storage both land here; the bar simply
      // forgets the dismissal.
    }
    if (dismissed) return;

    var hide = bar.querySelector('[data-ae-shop-hide]');
    if (hide) {
      hide.addEventListener('click', function () {
        bar.classList.remove('is-visible');
        dismissed = true;
        try {
          sessionStorage.setItem(storageKey, '1');
        } catch (err) {
          /* nothing to do */
        }
      });
    }

    /* Shown between the first product card and the footer, and never while
       the cookie banner is up — on a phone the two stack and swallow about
       half the viewport on first load. */
    var pastTrigger = false;
    var atFooter = false;

    // The consent banner is third-party and can appear, move or vanish at any
    // point, so its state is read fresh on each update rather than cached.
    function consentShowing() {
      var banner = document.getElementById('onetrust-banner-sdk');
      if (!banner || !banner.isConnected) return false;
      if (banner.getBoundingClientRect().height <= 0) return false;
      var visibility = window.getComputedStyle(banner);
      return visibility.visibility !== 'hidden' && visibility.display !== 'none';
    }

    function update() {
      if (dismissed) return;
      bar.classList.toggle('is-visible', pastTrigger && !atFooter && !consentShowing());
    }

    var supportsObserver = 'IntersectionObserver' in window;

    /* Reveal once the reader is past the first product card — far enough in
       to be shopping. Falls back to the hero, then to a scroll offset. */
    var trigger =
      document.querySelector('[data-ae-product]') ||
      document.querySelector('.ae-hero');

    if (trigger && supportsObserver) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            pastTrigger = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            update();
          });
        },
        { threshold: 0 }
      ).observe(trigger);
    } else {
      var offset = parseInt(bar.getAttribute('data-reveal-offset'), 10) || 600;
      window.addEventListener(
        'scroll',
        function () {
          pastTrigger = window.pageYOffset > offset;
          update();
        },
        { passive: true }
      );
    }

    // Re-check as soon as the banner is dismissed, rather than waiting for the
    // next scroll to happen to fire an update.
    if (typeof MutationObserver === 'function') {
      new MutationObserver(update).observe(document.body, {
        childList: true,
        subtree: false,
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    }

    // Retreat as soon as any of the footer shows, so it never covers it.
    var footer = document.querySelector('#shopify-section-footer, .footer, footer');
    if (footer && supportsObserver) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            atFooter = entry.isIntersecting;
            update();
          });
        },
        { threshold: 0 }
      ).observe(footer);
    }

    /* Mobile shows one product: whichever card sits nearest the middle of the
       viewport, falling back to the first. */

    var cards = Array.prototype.slice.call(document.querySelectorAll('[data-ae-product]'));
    if (!cards.length) return;

    function syncActive() {
      var middle = window.innerHeight / 2;
      var nearest = null;
      var shortest = Infinity;

      cards.forEach(function (card) {
        var box = card.getBoundingClientRect();
        var distance = Math.abs((box.top + box.bottom) / 2 - middle);
        if (distance < shortest) {
          shortest = distance;
          nearest = card.getAttribute('data-ae-product');
        }
      });

      var match = items.filter(function (item) {
        return item.getAttribute('data-handle') === nearest;
      })[0] || items[0];

      items.forEach(function (item) {
        if (item === match) item.setAttribute('data-active', '');
        else item.removeAttribute('data-active');
      });
    }

    var queued = false;
    window.addEventListener(
      'scroll',
      function () {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(function () {
          queued = false;
          syncActive();
        });
      },
      { passive: true }
    );

    syncActive();
  }

  /* ---- Boot -------------------------------------------------------------- */

  function init() {
    initCartForms();
    document.querySelectorAll('[data-ae-toc]').forEach(initTableOfContents);
    document.querySelectorAll('[data-ae-finder]').forEach(initFinder);
    document.querySelectorAll('[data-ae-shop-bar]').forEach(initShopBar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Re-run when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target;
    section.querySelectorAll('[data-ae-toc]').forEach(initTableOfContents);
    section.querySelectorAll('[data-ae-finder]').forEach(initFinder);
    section.querySelectorAll('[data-ae-shop-bar]').forEach(initShopBar);
  });
})();
